(function () {
  'use strict';

  /**
   * 商品点击埋点扩展。
   *
   * 依赖：
   * sensors-click-tracking.js 必须先加载，并提供 window.jiniuTracking。
   *
   * 职责：
   * 1. 识别主题、PageFly、预测搜索、购物车和推荐模块中的商品跳转。
   * 2. 补全商品、列表、模块、位置、搜索筛选和适配车型属性。
   * 3. 统一发布 product_click，并标记 eventType 为 click。
   *
   * 扩展新商品卡片时优先：
   * 1. 在 Liquid 中渲染 jiniu-product-click-attributes。
   * 2. 将新的卡片容器选择器加入 PRODUCT_ITEM_SELECTOR。
   * 3. 只有无法从 DOM 属性得到上下文时，才扩展推断逻辑。
   */
  const tracking = window.jiniuTracking;

  if (!tracking) {
    console.error('[SensorsData] Tracking core is unavailable');
    return;
  }

  const EVENT_ATTRIBUTE = 'data-jiniu-event';
  const PRODUCT_EVENT_NAME = 'product_click';

  // 所有可承载商品链接的卡片或列表项容器。
  const PRODUCT_ITEM_SELECTOR = [
    'product-card',
    '.grid-item',
    '.pf-slide.pf-c',
    '.predictive-search-list-item-product',
    '.article-recommendation-card',
    '.mini-cart-item',
    '.cart-item',
    '.addon-card',
    '.ymme-mini-card',
    '.cro-product',
    '.cro-side-product'
  ].join(',');
  const PRODUCT_LIST_ITEM_SELECTOR = [
    '.grid-item',
    '.pf-slide.pf-c',
    '.predictive-search-list-item-product',
    '.article-recommendation-card',
    '.mini-cart-item',
    '.cart-item',
    '.addon-card',
    '.ymme-mini-card',
    '.cro-product',
    '.cro-side-product'
  ].join(',');

  // product_click 固定输出这些字段；没有取到的值保持为空字符串。
  const PRODUCT_PROPERTY_NAMES = [
    'product_id',
    'variant_id',
    'msku',
    'product_name',
    'product_type',
    'product_price',
    'compare_at_price',
    'off',
    'currency',
    'list_type',
    'list_name',
    'module_name',
    'module_type',
    'module_position',
    'position',
    'row_index',
    'column_index',
    'click_area',
    'search_keyword',
    'filter_condition',
    'sort_type',
    'recommendation_type',
    'fitment_info'
  ];
  const NUMBER_PROPERTY_NAMES = new Set([
    'product_price',
    'compare_at_price',
    'off',
    'module_position',
    'position',
    'row_index',
    'column_index'
  ]);

  function createEmptyProductProperties() {
    return PRODUCT_PROPERTY_NAMES.reduce((properties, propertyName) => {
      properties[propertyName] = '';
      return properties;
    }, {});
  }

  // 仅把 Shopify 商品详情链接识别为商品跳转，并提取商品 handle。
  function getProductUrl(value) {
    if (!value) {
      return null;
    }

    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(
        /\/(?:collections\/[^/]+\/)?products\/([^/?#]+)\/?$/
      );

      if (!match) {
        return null;
      }

      return {
        handle: decodeURIComponent(match[1]),
        url
      };
    } catch (error) {
      return null;
    }
  }

  // 从实际点击目标向上查找商品链接，并排除色卡和商品详情页评分锚点。
  function resolveProductDestination(target) {
    if (target.closest('.card-product-color-swatch')) {
      return null;
    }

    const pageFlyElement = target.closest(
      '[data-action="url"][data-href]'
    );
    const pageFlyDestination = pageFlyElement
      ? getProductUrl(pageFlyElement.getAttribute('data-href'))
      : null;

    if (pageFlyDestination) {
      return {
        ...pageFlyDestination,
        element: pageFlyElement
      };
    }

    const anchor = target.closest('a[href]');
    const anchorDestination = anchor
      ? getProductUrl(anchor.getAttribute('href'))
      : null;

    if (anchorDestination) {
      const currentProduct = getProductUrl(window.location.href);
      const isCurrentProductReview =
        (window.jiniuTrackingContext?.pageType || '') === 'product' &&
        Boolean(
          target.closest(
            '.star-rating-link, .star-rating-badge, .jdgm-preview-badge'
          )
        ) &&
        currentProduct?.handle === anchorDestination.handle;

      if (isCurrentProductReview) {
        return null;
      }

      return {
        ...anchorDestination,
        element: anchor
      };
    }

    if (
      target.closest('.jdgm-preview-badge') &&
      (window.jiniuTrackingContext?.pageType || '') !== 'product'
    ) {
      const productCard = target.closest(
        'product-card, .card-product, .mlg-card'
      );
      const productLink = productCard?.querySelector(
        'a.js-product-link[href], a[href*="/products/"]'
      );
      const ratingDestination = productLink
        ? getProductUrl(productLink.getAttribute('href'))
        : null;

      if (ratingDestination) {
        return {
          ...ratingDestination,
          element: productLink
        };
      }
    }

    return null;
  }

  function toMoneyAmount(value) {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? amount / 100 : '';
  }

  // PageFly 卡片缺少 Liquid 埋点属性时，从其全局商品数据中补全基础字段。
  function getPageFlyProperties(destination) {
    const products = window.__pageflyProducts;

    if (!products || typeof products !== 'object') {
      return {};
    }

    const product = Object.values(products).find(
      (item) => item && item.handle === destination.handle
    );

    if (!product) {
      return {};
    }

    const variantId = destination.url.searchParams.get('variant') || '';
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant =
      variants.find((item) => String(item.id) === variantId) ||
      product.selected_or_first_available_variant ||
      product.selected_variant ||
      variants[0] ||
      null;
    const price = variant?.price;
    const compareAtPrice = variant?.compare_at_price;

    return {
      product_id:
        product.id === null || product.id === undefined
          ? ''
          : String(product.id),
      variant_id:
        variant?.id === null || variant?.id === undefined
          ? ''
          : String(variant.id),
      msku: variant?.sku || '',
      product_name: product.title || '',
      product_type: product.type || '',
      product_price: toMoneyAmount(price),
      compare_at_price: toMoneyAmount(compareAtPrice)
    };
  }

  // URL 中的 variant 参数优先于卡片默认变体。
  function getDestinationProperties(destination, properties) {
    const variantId = destination.url.searchParams.get('variant') || '';
    const nextProperties = {
      ...(properties.product_id ? {} : getPageFlyProperties(destination))
    };

    if (variantId) {
      if (
        properties.variant_id &&
        String(properties.variant_id) !== variantId &&
        !nextProperties.msku
      ) {
        nextProperties.msku = '';
      }

      nextProperties.variant_id = variantId;
    }

    if (!properties.product_name && !nextProperties.product_name) {
      const text = destination.element.textContent?.trim();
      const image = destination.element.matches('img')
        ? destination.element
        : destination.element.querySelector('img');

      nextProperties.product_name =
        text ||
        image?.getAttribute('title') ||
        image?.getAttribute('alt') ||
        '';
    }

    return nextProperties;
  }

  // 从当前 URL 和预测搜索框提取搜索词、筛选条件与排序方式。
  function getUrlContextProperties(element) {
    const parameters = new URLSearchParams(window.location.search);
    const filterCondition = {};
    const predictiveSearchInput = element
      .closest('predictive-search')
      ?.querySelector('input[name="q"]');

    parameters.forEach((value, key) => {
      if (!key.startsWith('filter.')) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(filterCondition, key)) {
        const currentValue = filterCondition[key];
        filterCondition[key] = Array.isArray(currentValue)
          ? [...currentValue, value]
          : [currentValue, value];
      } else {
        filterCondition[key] = value;
      }
    });

    return {
      search_keyword:
        predictiveSearchInput?.value.trim() ||
        parameters.get('q') ||
        window.jiniuTrackingContext?.searchTerms ||
        '',
      filter_condition: Object.keys(filterCondition).length
        ? JSON.stringify(filterCondition)
        : '',
      sort_type: parameters.get('sort_by') || ''
    };
  }

  // 模块位置按页面上可见的 Shopify section 和 PageFly section 计算。
  function getModuleElement(element) {
    return element.closest(
      '[data-section-id][data-pf-type="FlexSection"], .shopify-section, [id^="shopify-section-"], header, footer'
    );
  }

  function getModulePosition(element) {
    const moduleElement = getModuleElement(element);

    if (!moduleElement) {
      return '';
    }

    const modules = Array.from(
      document.querySelectorAll(
        '#MainContent > .shopify-section, #MainContent [data-section-id][data-pf-type="FlexSection"], body > header, body > footer'
      )
    ).filter((item) => item.getClientRects().length > 0);
    const index = modules.indexOf(moduleElement);

    return index >= 0 ? index + 1 : '';
  }

  function getModuleName(element) {
    const moduleElement = getModuleElement(element);

    if (!moduleElement) {
      return '';
    }

    const headings = Array.from(
      moduleElement.querySelectorAll('h1, h2, h3, h4, h5, h6')
    );
    const heading = headings.find(
      (item) =>
        !item.closest(PRODUCT_ITEM_SELECTOR) &&
        item.textContent &&
        item.textContent.trim()
    );

    return heading?.textContent.trim() || '';
  }

  function getVisibleProductItems(item) {
    const parent = item?.parentElement;

    if (!parent) {
      return [];
    }

    return Array.from(parent.children).filter((child) => {
      if (child.getClientRects().length === 0) {
        return false;
      }

      return (
        child.matches(PRODUCT_ITEM_SELECTOR) ||
        Boolean(child.querySelector(PRODUCT_ITEM_SELECTOR)) ||
        Boolean(
          child.querySelector(
            'a[href*="/products/"], [data-action="url"][data-href*="/products/"]'
          )
        )
      );
    });
  }

  // 根据可见元素的页面坐标计算列表位置、行号和列号。
  function getVisualPositionProperties(items, item, element) {
    const position = items.indexOf(item);

    if (position < 0) {
      return {
        module_position: getModulePosition(element),
        position: '',
        row_index: '',
        column_index: ''
      };
    }

    const rows = [];

    items.forEach((currentItem) => {
      const top = Math.round(currentItem.getBoundingClientRect().top);
      let row = rows.find((currentRow) => Math.abs(currentRow.top - top) <= 4);

      if (!row) {
        row = { top, items: [] };
        rows.push(row);
      }

      row.items.push(currentItem);
    });

    rows.sort((firstRow, secondRow) => firstRow.top - secondRow.top);
    rows.forEach((row) => {
      row.items.sort(
        (firstItem, secondItem) =>
          firstItem.getBoundingClientRect().left -
          secondItem.getBoundingClientRect().left
      );
    });

    const rowIndex = rows.findIndex((row) => row.items.includes(item));
    const columnIndex =
      rowIndex >= 0 ? rows[rowIndex].items.indexOf(item) : -1;

    return {
      module_position: getModulePosition(element),
      position: position + 1,
      row_index: rowIndex >= 0 ? rowIndex + 1 : '',
      column_index: columnIndex >= 0 ? columnIndex + 1 : ''
    };
  }

  function getPositionProperties(element) {
    const item =
      element.closest(PRODUCT_LIST_ITEM_SELECTOR) ||
      element.closest('product-card');

    if (!item) {
      const action = element.closest(
        'a[href], [data-action="url"][data-href]'
      );
      const container = getModuleElement(element) || document.body;
      const actions = Array.from(
        container.querySelectorAll(
          'a[href], [data-action="url"][data-href]'
        )
      ).filter((currentAction) => {
        const value = currentAction.matches('a')
          ? currentAction.getAttribute('href')
          : currentAction.getAttribute('data-href');
        return (
          currentAction.getClientRects().length > 0 &&
          Boolean(getProductUrl(value))
        );
      });

      return getVisualPositionProperties(actions, action, element);
    }

    const items = getVisibleProductItems(item);
    return getVisualPositionProperties(items, item, element);
  }

  // 根据点击目标的标签和类名识别图片、标题、按钮等点击区域。
  function inferClickArea(target, destinationElement) {
    const element = target.closest(
      'img, button, [role="button"], a, .hot_dot_content'
    );
    const className = `${element?.className || ''} ${
      destinationElement?.className || ''
    }`.toLowerCase();

    if (
      target.closest(
        '.star-rating-link, .star-rating-badge, .jdgm-preview-badge'
      )
    ) {
      return 'review';
    }

    if (
      target.closest('img, picture') ||
      /image|media|thumbnail|img/.test(className)
    ) {
      return 'image';
    }

    if (
      target.closest('button, [role="button"]') ||
      /button|btn|view-details|atc/.test(className)
    ) {
      return 'button';
    }

    if (
      target.closest('h1, h2, h3, h4, h5, h6') ||
      /title|heading|pname/.test(className)
    ) {
      return 'product_name';
    }

    if (target.closest('.hot_dot_content, .hot_dot_pin')) {
      return 'hotspot';
    }

    if (target.closest('.slideshow-media, [data-pf-type="Image5"]')) {
      return 'banner';
    }

    if (target.closest('header, footer, nav')) {
      return 'navigation';
    }

    return 'card';
  }

  // DOM 显式属性优先；这里只补全尚未提供的列表和模块上下文。
  function inferContextProperties(element, properties) {
    const pageType = window.jiniuTrackingContext?.pageType || '';
    const source =
      element.closest('[data-jiniu-source]')?.getAttribute('data-jiniu-source') ||
      '';
    const moduleName = getModuleName(element);
    const inferred = {};

    if (element.closest('.predictive-search-list-item-product')) {
      inferred.list_type = 'search';
      inferred.list_name = 'Predictive search results';
      inferred.module_name = 'Predictive search';
      inferred.module_type = 'search_results';
      inferred.recommendation_type = 'search product';
    } else if (
      element.closest(
        '.article-recommendation-card, .article-recommendations-desktop, .article-recommendations-mobile'
      )
    ) {
      inferred.list_type = 'recommendation';
      inferred.list_name = moduleName || 'Article product recommendations';
      inferred.module_name = moduleName || 'Article recommendations';
      inferred.module_type = 'product_recommendations';
      inferred.recommendation_type = 'blog products';
    } else if (element.closest('.mini-cart-item')) {
      inferred.list_type = 'cart';
      inferred.list_name = 'Mini cart';
      inferred.module_name = 'Mini cart';
      inferred.module_type = 'cart_list';
    } else if (element.closest('.cart-item')) {
      inferred.list_type = 'cart';
      inferred.list_name = 'Cart';
      inferred.module_name = 'Cart products';
      inferred.module_type = 'cart_list';
    } else if (element.closest('.order-details')) {
      inferred.list_type = 'order';
      inferred.list_name = 'Order products';
      inferred.module_name = 'Order details';
      inferred.module_type = 'order_list';
    } else if (
      element.closest(
        '.custom-addons-wrapper, [data-addon-drawer], .ymme-mini-grid, product-recommendations'
      ) ||
      source === 'ProductRecommendations'
    ) {
      inferred.list_type = 'recommendation';
      inferred.list_name = moduleName || 'Related products';
      inferred.module_name = moduleName || 'Related products';
      inferred.module_type = element.closest('.ymme-mini-grid')
        ? 'fitment_recommendations'
        : 'product_recommendations';
      inferred.recommendation_type = 'Related products';
    } else if (element.closest('.dk-hub, .dpf-b2b-page')) {
      inferred.list_type = 'landing_page';
      inferred.list_name = moduleName || document.title;
      inferred.module_name = moduleName || 'Product table';
      inferred.module_type = 'product_table';
    } else if (element.closest('.hot_dot_pin, .slideshow-media')) {
      inferred.list_type = 'campaign';
      inferred.list_name = moduleName || document.title;
      inferred.module_name = moduleName || 'Campaign';
      inferred.module_type = element.closest('.hot_dot_pin')
        ? 'hotspot'
        : 'banner';
    } else if (element.closest('header, footer, nav')) {
      inferred.list_type = 'navigation';
      inferred.list_name = element.closest('footer') ? 'Footer' : 'Header';
      inferred.module_name = inferred.list_name;
      inferred.module_type = 'navigation';
    } else if (element.closest('[data-pf-type="ProductList2"]')) {
      inferred.list_type = 'collection';
      inferred.list_name = moduleName || 'PageFly product list';
      inferred.module_name = moduleName || 'PageFly product list';
      inferred.module_type = 'product_carousel';
    } else if (element.closest('[data-pf-type="Image5"]')) {
      inferred.list_type = 'campaign';
      inferred.list_name = moduleName || document.title;
      inferred.module_name = moduleName || 'PageFly campaign';
      inferred.module_type = 'banner';
    } else if (source.includes('SearchPage') || pageType === 'search') {
      inferred.list_type = 'search';
      inferred.list_name = 'Search results';
      inferred.module_name = moduleName || 'Search results';
      inferred.module_type = 'product_grid';
    } else if (pageType === 'article' || pageType === 'blog') {
      inferred.list_type = 'recommendation';
      inferred.list_name = moduleName || 'Blog products';
      inferred.module_name = moduleName || 'Blog products';
      inferred.module_type = 'product_recommendations';
      inferred.recommendation_type = 'blog products';
    } else if (pageType === 'product') {
      inferred.list_type = 'recommendation';
      inferred.list_name = moduleName || 'Related products';
      inferred.module_name = moduleName || 'Related products';
      inferred.module_type = 'product_recommendations';
      inferred.recommendation_type = 'Related products';
    } else if (
      source.includes('FeaturedCollection') ||
      source.includes('TabbedProductLists')
    ) {
      inferred.list_type = 'featured_collection';
      inferred.list_name = moduleName || properties.module_name || '';
      inferred.module_name = moduleName || properties.module_name || '';
      inferred.module_type = element.closest('carousel-slider, .scroller')
        ? 'product_carousel'
        : 'product_grid';
    } else if (source.includes('Collection') || pageType === 'collection') {
      inferred.list_type = 'collection';
      inferred.list_name =
        window.jiniuTrackingContext?.resourceTitle ||
        source.replace(/^\s*-\s*Collection:\s*/, '') ||
        moduleName;
      inferred.module_name = moduleName || inferred.list_name;
      inferred.module_type = 'product_grid';
    } else {
      inferred.list_type = 'product_list';
      inferred.list_name = moduleName || document.title;
      inferred.module_name = moduleName || '';
      inferred.module_type = element.closest('.scroller, carousel-slider')
        ? 'product_carousel'
        : 'product_grid';
    }

    return Object.keys(inferred).reduce((result, key) => {
      if (!properties[key]) {
        result[key] = inferred[key];
      }
      return result;
    }, {});
  }

  // 神策接收字符串形式的 JSON 数组，兼容 Liquid JSON 与竖线分隔文本。
  function normalizeFitmentInfo(value) {
    if (value === '' || value === null || value === undefined) {
      return '';
    }

    let normalizedValue = value;

    if (typeof value === 'string') {
      try {
        normalizedValue = JSON.parse(value);
      } catch (error) {
        normalizedValue = value;
      }
    }

    if (typeof normalizedValue === 'string') {
      normalizedValue = normalizedValue
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);
    } else if (!Array.isArray(normalizedValue)) {
      normalizedValue = [String(normalizedValue)];
    }

    return JSON.stringify(normalizedValue);
  }

  // 统一字段类型，并在缺少 off 时根据原价和售价计算折扣百分比。
  function normalizeProductProperties(properties) {
    const normalized = {
      ...createEmptyProductProperties(),
      ...properties
    };

    PRODUCT_PROPERTY_NAMES.forEach((propertyName) => {
      const value = normalized[propertyName];

      if (NUMBER_PROPERTY_NAMES.has(propertyName)) {
        if (value === '' || value === null || value === undefined) {
          normalized[propertyName] = '';
          return;
        }

        const numberValue = Number(value);
        normalized[propertyName] = Number.isFinite(numberValue)
          ? numberValue
          : '';
        return;
      }

      if (propertyName === 'fitment_info') {
        normalized[propertyName] = normalizeFitmentInfo(value);
        return;
      }

      normalized[propertyName] =
        value === null || value === undefined ? '' : String(value);
    });

    if (
      normalized.off === '' &&
      normalized.compare_at_price > normalized.product_price &&
      normalized.compare_at_price > 0
    ) {
      normalized.off = Math.round(
        ((normalized.compare_at_price - normalized.product_price) /
          normalized.compare_at_price) *
          100
      );
    }

    return normalized;
  }

  // product_click 由本脚本接管，阻止公共点击监听器重复上报同一事件。
  tracking.claimClickEvent(PRODUCT_EVENT_NAME);

  // 使用捕获阶段，确保页面跳转前完成属性收集和 Shopify 事件发布。
  document.addEventListener(
    'click',
    function (event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const explicitTarget = event.target.closest(`[${EVENT_ATTRIBUTE}]`);
      const destination = resolveProductDestination(event.target);
      const explicitEventName =
        explicitTarget?.getAttribute(EVENT_ATTRIBUTE) || '';

      if (!destination && explicitEventName !== PRODUCT_EVENT_NAME) {
        return;
      }

      const trackingElement =
        explicitTarget || destination?.element || event.target;
      const eventTarget = event.target;

      // 延迟到当前点击监听结束后执行，让其他监听器有机会 preventDefault。
      const publishProductClick = () => {
        if (event.defaultPrevented) {
          return;
        }

        let properties = tracking.getInheritedProperties(trackingElement);

        if (destination) {
          properties = {
            ...properties,
            ...getDestinationProperties(destination, properties)
          };
        }

        const positionProperties = getPositionProperties(trackingElement);
        Object.keys(positionProperties).forEach((propertyName) => {
          if (!properties[propertyName]) {
            properties[propertyName] = positionProperties[propertyName];
          }
        });

        properties = normalizeProductProperties({
          ...properties,
          ...getUrlContextProperties(trackingElement),
          ...inferContextProperties(trackingElement, properties),
          click_area: inferClickArea(
            eventTarget,
            destination?.element || trackingElement
          ),
          currency:
            properties.currency ||
            window.jiniuTrackingContext?.currency ||
            ''
        });

        tracking.publish(PRODUCT_EVENT_NAME, properties, 'click');
      };

      if (typeof window.queueMicrotask === 'function') {
        window.queueMicrotask(publishProductClick);
      } else {
        Promise.resolve().then(publishProductClick);
      }
    },
    { capture: true }
  );
})();
